// @ts-check
import { TOPIC } from 'Util/Message/TopicName';

import { CONTENT_GROUP, EVENT_NAME } from './Event';

/**
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 * @param {Message} props
 * @returns {MeasurementProtocolEvent}
 */
export default function createMeasurementProtocolEvent({ topic, info }) {
  switch (topic) {
    //#region POPUP_ANNOUNCEMENT
    case TOPIC.POPUP_ANNOUNCEMENT_OCCURRENCE: {
      return {
        name: EVENT_NAME.CUSTOM.ANNOUNCEMENT_OCCURRENCE,
        params: {
          content_group: CONTENT_GROUP.POPUP_ANNOUNCEMENT,
          content_type: 'campaign',
          content_id: info.campaignName,
        },
      };
    }
    case TOPIC.POPUP_ANNOUNCEMENT_ON_CLOSE: {
      return {
        name: EVENT_NAME.CUSTOM.ANNOUNCEMENT_CLOSE,
        params: {
          content_group: CONTENT_GROUP.POPUP_ANNOUNCEMENT,
          content_type: 'campaign',
          content_id: info.campaignName,
        },
      };
    }
    case TOPIC.POPUP_ANNOUNCEMENT_MORE_THAN_TEN_SECOND: {
      return {
        name: EVENT_NAME.CUSTOM.ANNOUNCEMENT_READ,
        params: {
          content_group: CONTENT_GROUP.POPUP_ANNOUNCEMENT,
          content_type: 'campaign',
          content_id: info.campaignName,
          method: 'view_more_than_10_sec',
        },
      };
    }
    case TOPIC.POPUP_ANNOUNCEMENT_LAST_SLIDE: {
      return {
        name: EVENT_NAME.CUSTOM.ANNOUNCEMENT_READ,
        params: {
          content_group: CONTENT_GROUP.POPUP_ANNOUNCEMENT,
          content_type: 'campaign',
          content_id: info.campaignName,
          method: 'view_last_slide',
        },
      };
    }
    case TOPIC.POPUP_ANNOUNCEMENT_CALL_TO_ACTION: {
      return {
        name: EVENT_NAME.CUSTOM.ANNOUNCEMENT_READ,
        params: {
          content_group: CONTENT_GROUP.POPUP_ANNOUNCEMENT,
          content_type: 'campaign',
          content_id: info.campaignName,
          method: 'click_call_to_action',
        },
      };
    }
    case TOPIC.POPUP_ANNOUNCEMENT_NOT_SHOW_AGAIN: {
      return {
        name: EVENT_NAME.CUSTOM.ANNOUNCEMENT_READ,
        params: {
          content_group: CONTENT_GROUP.POPUP_ANNOUNCEMENT,
          content_type: 'campaign',
          content_id: info.campaignName,
          method: 'click_do_not_show_again',
        },
      };
    }
    //#endregion

    //#region NOTIFICATION
    case TOPIC.NOTIFICATION_CLICKED: {
      return {
        name: EVENT_NAME.SELECT_CONTENT,
        params: {
          content_group: CONTENT_GROUP.NOTIFICATION,
          content_type: 'menu',
          content_id: 'notification',
        },
      };
    }
    case TOPIC.NOTIFICATION_ITEM_CLICKED: {
      return {
        name: EVENT_NAME.SELECT_CONTENT,
        params: {
          content_group: CONTENT_GROUP.NOTIFICATION,
          content_type: 'campaign',
          content_id: info.notificationTitle,
        },
      };
    }
    case TOPIC.NOTIFICATION_TAB_CLICKED: {
      return {
        name: EVENT_NAME.SELECT_CONTENT,
        params: {
          content_group: CONTENT_GROUP.NOTIFICATION,
          content_type: 'notification_tab',
          content_id: info.tabTitle,
        },
      };
    }
    //#endregion

    //#region CLASSROOM
    case TOPIC.CLASSROOM_HEARTBEAT: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_HEARTBEAT,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          content_type: 'subsection',
          content_id: info.subsectionId,
          course_id: info.courseId,
          revision: info.revision,
          course_mode: info.courseMode,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          subsection_content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
        },
      };
    }
    case TOPIC.CLASSROOM_TOC_TOGGLE_CLICKED: {
      return {
        name: EVENT_NAME.SELECT_CONTENT,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          content_type: 'menu',
          content_id: 'ToC',
          course_id: info.courseId,
          course_code: info.courseCode,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_DOCUMENT_TOGGLE_CLICKED: {
      return {
        name: EVENT_NAME.SELECT_CONTENT,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          content_type: 'menu',
          content_id: 'document',
          course_id: info.courseId,
          course_code: info.courseCode,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_SECTION_CLICKED: {
      // FIXME: GA not accept long thai character
      const contentId = /** @type {string} */ (info.sectionTitle).substring(0, 32);
      return {
        name: EVENT_NAME.SELECT_CONTENT,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          content_type: 'course_section',
          content_id: contentId,
          course_id: info.courseId,
          course_code: info.courseCode,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_SUBSECTION_CLICKED: {
      // FIXME: GA not accept long thai character
      const contentId = /** @type {string} */ (info.subsectionTitle).substring(0, 32);
      return {
        name: EVENT_NAME.SELECT_CONTENT,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          content_type: 'course_subsection',
          content_id: contentId,
          course_id: info.courseId,
          course_code: info.courseCode,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_DOWNLOAD_ITEM: {
      return {
        name: EVENT_NAME.FILE_DOWNLOAD,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          file_name: info.itemTitle,
          course_id: info.courseId,
          course_code: info.courseCode,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_USE_ROOTED_DEVICE: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_TRY_INVALID_ACTION,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          method: 'use_rooted_device',
          course_id: info.courseId,
          course_code: info.courseCode,
        },
      };
    }
    case TOPIC.CLASSROOM_USE_EMULATOR: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_TRY_INVALID_ACTION,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          method: 'use_emulator',
          course_id: info.courseId,
          course_code: info.courseCode,
        },
      };
    }
    case TOPIC.CLASSROOM_RECORD_SCREEN: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_TRY_INVALID_ACTION,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          method: 'record_screen',
          course_id: info.courseId,
          course_code: info.courseCode,
        },
      };
    }
    case TOPIC.CLASSROOM_MULTIPLE_SCREEN: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_TRY_INVALID_ACTION,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          method: 'multiple_screen',
          course_id: info.courseId,
          course_code: info.courseCode,
        },
      };
    }
    case TOPIC.CLASSROOM_SUBSECTION_VIDEO_COMPLETE: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_SUBSECTION_VIDEO_COMPLETE,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          course_id: info.courseId,
          revision: info.revision,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          video_provider: info.videoProvider,
          server_timestamp: info.serverTimestamp,
          video_watched_duration: info.videoWatchedDuration,
          video_completion_threshold: info.videoCompletionThreshold,
          video_total_duration: info.videoTotalDuration,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_SUBSECTION_VIDEO_COMPLETE_CLIENT: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_SUBSECTION_VIDEO_COMPLETE_CLIENT,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          course_id: info.courseId,
          revision: info.revision,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          video_provider: info.videoProvider,
          video_watched_duration: info.videoWatchedDuration,
          video_completion_threshold: info.videoCompletionThreshold,
          video_total_duration: info.videoTotalDuration,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_SUBSECTION_VIDEO_COMPLETE_ERROR: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_SUBSECTION_VIDEO_COMPLETE_ERROR,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          course_id: info.courseId,
          revision: info.revision,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          video_provider: info.videoProvider,
          error_message: info.errorMessage,
          video_watched_duration: info.videoWatchedDuration,
          video_completion_threshold: info.videoCompletionThreshold,
          video_total_duration: info.videoTotalDuration,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_SUBSECTION_VIDEO_ON_VIDEO_PLAYER_ERROR: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_SUBSECTION_VIDEO_ON_VIDEO_PLAYER_ERROR,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          course_id: info.courseId,
          revision: info.revision,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          video_provider: info.videoProvider,
          error_message: info.errorMessage,
          video_watched_duration: info.videoWatchedDuration,
          video_completion_threshold: info.videoCompletionThreshold,
          video_total_duration: info.videoTotalDuration,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.CLASSROOM_SUBSECTION_VIDEO_INTERACTION: {
      const baseParams = {
        course_id: info.courseId,
        revision: info.revision,
        subsection_id: info.subsectionId,
        timestamp: info.clientTimestamp,
        course_mode: info.courseMode,
      };
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_SUBSECTION_VIDEO_INTERACTION,
        params: {
          ...baseParams,
          content_group: CONTENT_GROUP.CLASSROOM,
          action: info.action,
          video_provider: info.videoProvider,
          video_current_sec: info.videoCurrentSec,
          video_threshold_sec: info.videoThresholdSec,
          video_duration_sec: info.videoDurationSec,
          video_current_percent: info.videoCurrentPercent,
          video_total_watch_sec: info.videoTotalWatchSec,
          video_completion_status: info.videoCompletionStatus,
          interaction_method: info.interactionMethod,
          ...((info.action === 'seek_forward' || info.action === 'seek_backward') && {
            seek_from_sec: info.seekFromSec,
            seek_to_sec: info.seekToSec,
          }),
          ...(info.action === 'volume_change' && {
            change_volume_from: info.changeVolumeFrom,
            change_volume_to: info.changeVolumeTo,
          }),
          ...(info.action === 'speed_change' && {
            speed: info.speed,
          }),
        },
      };
    }
    case TOPIC.CLASSROOM_SUBSECTION_CONTENT_CLICKED: {
      return {
        name: EVENT_NAME.CUSTOM.CLASSROOM_SUBSECTION_CONTENT_CLICKED,
        params: {
          content_group: CONTENT_GROUP.CLASSROOM,
          course_id: info.courseId,
          revision: info.revision,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          course_mode: info.courseMode,
        },
      };
    }
    case TOPIC.MYPATH_CLICKED: {
      return {
        name: EVENT_NAME.CUSTOM.MYPATH_CLICKED,
        params: {
          content_group: CONTENT_GROUP.MYPATH,
        },
      };
    }
    //#endregion

    //#region QUIZ
    case TOPIC.QUIZ_START: {
      return {
        name: EVENT_NAME.CUSTOM.QUIZ_START,
        params: {
          content_group: CONTENT_GROUP.QUIZ,
          course_id: info.courseId,
          revision: info.revision,
          quiz_id: info.quizId,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          course_mode: info.courseMode,
        },
      };
    }

    case TOPIC.QUIZ_SUBMIT: {
      return {
        name: EVENT_NAME.CUSTOM.QUIZ_SUBMIT,
        params: {
          content_group: CONTENT_GROUP.QUIZ,
          course_id: info.courseId,
          revision: info.revision,
          quiz_id: info.quizId,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          course_mode: info.courseMode,
        },
      };
    }

    case TOPIC.QUIZ_SUBMIT_ERROR: {
      return {
        name: EVENT_NAME.CUSTOM.QUIZ_SUBMIT_ERROR,
        params: {
          content_group: CONTENT_GROUP.QUIZ,
          course_id: info.courseId,
          revision: info.revision,
          quiz_id: info.quizId,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          error_message: info.errorMessage,
          course_mode: info.courseMode,
        },
      };
    }

    case TOPIC.QUIZ_START_ERROR: {
      return {
        name: EVENT_NAME.CUSTOM.QUIZ_START_ERROR,
        params: {
          content_group: CONTENT_GROUP.QUIZ,
          course_id: info.courseId,
          revision: info.revision,
          quiz_id: info.quizId,
          subsection_id: info.subsectionId,
          subsection_title: info.subsectionTitle,
          content_type: info.subsectionContentType,
          timestamp: info.clientTimestamp,
          error_message: info.errorMessage,
          course_mode: info.courseMode,
        },
      };
    }
    //#endregion

    //#region FAQ
    case TOPIC.FAQ_OPEN_PANEL: {
      return {
        name: EVENT_NAME.CUSTOM.FAQ_OPEN_PANEL,
        params: {
          content_group: CONTENT_GROUP.FAQ,
          course_code: info.courseCode,
          course_id: info.courseId,
          asset_id: info.assetId,
          asset_name: info.assetName,
        },
      };
    }
    case TOPIC.FAQ_CLOSE_PANEL: {
      return {
        name: EVENT_NAME.CUSTOM.FAQ_CLOSE_PANEL,
        params: {
          content_group: CONTENT_GROUP.FAQ,
          course_code: info.courseCode,
          course_id: info.courseId,
          asset_id: info.assetId,
          asset_name: info.assetName,
          faq_id: info.faqId,
          faq_title: info.faqTitle,
        },
      };
    }
    case TOPIC.FAQ_ITEM_CLICKED: {
      return {
        name: EVENT_NAME.CUSTOM.FAQ_ITEM_CLICKED,
        params: {
          content_group: CONTENT_GROUP.FAQ,
          course_code: info.courseCode,
          course_id: info.courseId,
          asset_id: info.assetId,
          asset_name: info.assetName,
          faq_id: info.faqId,
          faq_title: info.faqTitle,
        },
      };
    }
    case TOPIC.FAQ_ITEM_BACK_BUTTON_CLICKED: {
      return {
        name: EVENT_NAME.CUSTOM.FAQ_ITEM_BACK_BUTTON_CLICKED,
        params: {
          content_group: CONTENT_GROUP.FAQ,
          course_code: info.courseCode,
          course_id: info.courseId,
          asset_id: info.assetId,
          asset_name: info.assetName,
          faq_id: info.faqId,
          faq_title: info.faqTitle,
        },
      };
    }
    case TOPIC.FAQ_VOTE_ITEM: {
      return {
        name: EVENT_NAME.CUSTOM.FAQ_VOTE_ITEM,
        params: {
          content_group: CONTENT_GROUP.FAQ,
          course_code: info.courseCode,
          course_id: info.courseId,
          asset_id: info.assetId,
          asset_name: info.assetName,
          faq_id: info.faqId,
          faq_title: info.faqTitle,
          rating: info.rating,
          rating_detail: info.ratingDetail,
        },
      };
    }
    case TOPIC.FAQ_OPEN_CLEAR: {
      return {
        name: EVENT_NAME.CUSTOM.FAQ_OPEN_CLEAR,
        params: {
          content_group: CONTENT_GROUP.FAQ,
          course_code: info.courseCode,
          course_id: info.courseId,
          asset_id: info.assetId,
          asset_name: info.assetName,
          faq_id: info.faqId,
          faq_title: info.faqTitle,
        },
      };
    }
    case TOPIC.FAQ_SUBMITTED_CLEAR_TICKET: {
      return {
        name: EVENT_NAME.CUSTOM.FAQ_SUBMITTED_CLEAR_TICKET,
        params: {
          content_group: CONTENT_GROUP.FAQ,
          course_code: info.courseCode,
          course_id: info.courseId,
          asset_id: info.assetId,
          asset_name: info.assetName,
          faq_id: info.faqId,
          faq_title: info.faqTitle,
          ticket_id: info.ticketId,
        },
      };
    }
    //#endregion

    default: {
      return null;
    }
  }
}

/** @typedef {import('Util/Message/postMessageHelper').Message} Message */
/**
 * @typedef MeasurementProtocolEvent
 * @property {string} name
 * @property {Record<string, any>} params
 */
